import { useMemo } from "react";

import { useActiveAccounts } from "@/hooks/useActiveAccounts";
import { useBalances } from "@/hooks/useBalances";
import useBaseCurrencyConverter from "@/hooks/useBaseCurrencyConverter";
import { useCustomerAccount, useGlobalData } from "@/providers";
import {
  buildAccountSummaryItems,
  collectAccountPageCodes,
  collectAccountSummaryCodes,
  filterEntityScopedBalances,
  shouldShowViewAllAccountsCard,
} from "@/utils/buildAccountSummary";

export function useAccountSummary() {
  const {
    balances,
    isLoading: isBalancesLoading,
    isError: isBalancesError,
    refetch,
    isUninitialized,
  } = useBalances();
  const { products, isLoadingProducts } = useGlobalData();
  const { convert, convertAndSum, isPricesLoading } =
    useBaseCurrencyConverter();
  const { customerAccountBaseCurrency, hasCurrencies, hasDigitalAssets } =
    useCustomerAccount();
  const {
    accounts,
    availableAccounts,
    tradableOnly,
    isLoading: isActiveAccountsLoading,
  } = useActiveAccounts();

  const baseCurrency = customerAccountBaseCurrency;
  const isLoading = isBalancesLoading || isLoadingProducts;
  const entityScope = useMemo(
    () => ({ hasCurrencies, hasDigitalAssets }),
    [hasCurrencies, hasDigitalAssets]
  );

  const summaryItems = useMemo(
    () =>
      buildAccountSummaryItems({
        balances,
        products: products ?? undefined,
        ...entityScope,
        baseCurrency,
        convert,
      }),
    [balances, products, entityScope, baseCurrency, convert]
  );

  const totalBalance = useMemo(() => {
    const entries = filterEntityScopedBalances(
      balances,
      products ?? undefined,
      entityScope
    );

    return convertAndSum(
      entries.map((entry) => ({
        amount: entry.balance,
        currency: entry.code,
      }))
    );
  }, [balances, products, entityScope, convertAndSum]);

  const allAccountsPageCodes = useMemo(
    () => collectAccountPageCodes(accounts, availableAccounts, tradableOnly),
    [accounts, availableAccounts, tradableOnly]
  );

  const showViewAllAccountsCard = useMemo(() => {
    if (isActiveAccountsLoading) return false;

    return shouldShowViewAllAccountsCard({
      summaryCodes: collectAccountSummaryCodes(summaryItems),
      allAccountsPageCodes,
    });
  }, [isActiveAccountsLoading, summaryItems, allAccountsPageCodes]);

  return {
    summaryItems,
    totalBalance,
    showViewAllAccountsCard,
    baseCurrency,
    isLoading,
    isStillCalculating: isPricesLoading || isLoading,
    isError: isBalancesError,
    refetch,
    isUninitialized,
  };
}
